import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CoverageGapIllustration,
  TimeOffConflictIllustration,
  PrintFriendlyIllustration,
  CanadaHostedIllustration,
  FastSetupIllustration,
} from '@/components/illustrations';
import { TIER_CATALOG, TRIAL_DAYS, formatCAD } from '@/lib/billing';
import { getCatalog, type SubscriptionPlan } from '@/lib/catalog';
import type { SubscriptionTier } from '@/lib/types';

/**
 * Public landing page at the apex (pharma-sync.ca).
 *
 * Audience: Canadian independent-pharmacy owners and managers landing here
 * cold from search, a partner referral, or a security-vendor URL crawler.
 * They have no PharmaSync context and 8 seconds of patience. The page has
 * to do three things at once:
 *   1. Make the value prop legible in one sentence (hero).
 *   2. Earn trust quickly (Canadian hosting, audit log, no fake metrics).
 *   3. Hand off cleanly to either signup or pricing.
 *
 * Sections, top to bottom:
 *   1. Header — logo + "Sign in" link
 *   2. Hero — headline, subhead, two CTAs, pilot pill
 *   3. Problem statement — one short paragraph
 *   4. Features — five branded illustration cards (shared with SignIn carousel)
 *   5. How it works — three numbered steps
 *   6. Trust strip — four short pills (CA hosting, PIPEDA, RLS, audit log)
 *   7. Pricing teaser — three tiers pulling from catalog, link to /pricing
 *   8. Founder note — italic blockquote, brand-teal left rule
 *   9. FAQ — accordion with three short Q&As
 *   10. Final CTA — emotional close + start-pilot button
 *   11. Footer — legal + contact links
 *
 * Routing: this is rendered from App.tsx's `/` route ONLY for unauthenticated
 * visitors. Authenticated users at `/` get the in-app Home (dashboard) instead.
 *
 * Why a custom layout instead of <BrandedLayout>: BrandedLayout is sized for
 * the auth/onboarding/legal pages where a tight masthead and single legal-link
 * footer is appropriate. The landing page wants more rhythm — full-bleed
 * tinted sections, alternating backgrounds, generous vertical breathing room.
 *
 * Illustration set is identical to /auth/sign-in's marketing pane, so the
 * brand reads consistently from cold visit through to the dashboard.
 */
export default function Landing() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);

  useEffect(() => {
    let cancelled = false;
    getCatalog()
      .then((p) => { if (!cancelled && p.length > 0) setPlans(p); })
      .catch(() => { /* fall back to TIER_CATALOG seed via planFor() */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground"
      >
        Skip to main content
      </a>

      {/* ===== HEADER ===== */}
      <header className="border-b border-border" style={{ backgroundColor: 'hsl(var(--brand-sky) / 0.18)' }}>
        <div className="container mx-auto flex items-center justify-between gap-4 py-4 px-4">
          <Link
            to="/"
            className="flex items-center gap-3 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label="PharmaSync home"
          >
            <img src="/logo.svg" alt="" className="h-9 w-9" aria-hidden="true" />
            <h1 className="text-lg font-semibold tracking-tight leading-none">
              <span style={{ color: 'hsl(var(--brand-navy))' }}>Pharma</span>
              <span style={{ color: 'hsl(var(--brand-teal))' }}>Sync</span>
            </h1>
          </Link>
          <Link
            to="/auth/sign-in"
            className="text-sm font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
            style={{ color: 'hsl(var(--brand-navy))' }}
          >
            Sign in
          </Link>
        </div>
      </header>

      <main id="main-content" tabIndex={-1}>

        {/* ===== HERO ===== */}
        <section
          className="border-b border-border"
          style={{ backgroundColor: 'hsl(var(--brand-sky) / 0.18)' }}
          aria-labelledby="hero-h"
        >
          <div className="container mx-auto px-4 py-16 lg:py-24">
            <div className="max-w-3xl space-y-6">
              <h2
                id="hero-h"
                className="text-4xl lg:text-5xl font-semibold tracking-tight leading-tight"
                style={{ color: 'hsl(var(--brand-navy))' }}
              >
                Schedule your pharmacy in minutes, not weekends.
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
                Built for Canadian independent pharmacies. Hosted in Canada. PIPEDA-aligned by default.
              </p>
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Link
                  to="/auth/sign-up"
                  className="inline-block rounded-md px-6 py-3 text-sm font-semibold text-white hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  style={{ backgroundColor: 'hsl(var(--brand-navy))' }}
                >
                  Start your pilot
                </Link>
                <Link
                  to="/pricing"
                  className="inline-block rounded-md border-2 px-6 py-3 text-sm font-semibold hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-white"
                  style={{ borderColor: 'hsl(var(--brand-teal))', color: 'hsl(var(--brand-teal))' }}
                >
                  See pricing
                </Link>
                <span
                  className="inline-block rounded-full bg-white px-3 py-1 text-xs font-medium border border-border"
                  style={{ color: 'hsl(var(--brand-navy))' }}
                >
                  Currently in private pilot
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ===== PROBLEM ===== */}
        <section className="container mx-auto px-4 py-16" aria-labelledby="problem-h">
          <div className="max-w-3xl space-y-4">
            <h2 id="problem-h" className="text-2xl font-semibold tracking-tight">
              The problem we solve
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground">
              Independent pharmacies coordinate schedules in spreadsheets, group chats, and
              printouts. Mistakes mean coverage gaps that touch patient safety. PharmaSync
              replaces all of it with one source of truth: schedules, time-off, and coverage
              detection in one place &mdash; tuned for Canadian pharmacies, audited under
              Canadian privacy law.
            </p>
          </div>
        </section>

        {/* ===== FEATURES ===== */}
        <section
          className="border-y border-border"
          style={{ backgroundColor: 'hsl(var(--brand-sky) / 0.10)' }}
          aria-labelledby="features-h"
        >
          <div className="container mx-auto px-4 py-16">
            <div className="max-w-3xl mb-10">
              <h2 id="features-h" className="text-2xl font-semibold tracking-tight">
                What you get
              </h2>
              <p className="text-base text-muted-foreground mt-2">
                Five product surfaces. Built deliberately. No bloat.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <FeatureCard
                title="Coverage gap detection"
                body="Days without a pharmacist on duty are flagged amber as you build the month. You catch them at 11am Tuesday, not 8am Saturday."
                Illustration={CoverageGapIllustration}
              />
              <FeatureCard
                title="Time-off conflict surfacing"
                body="Open a request and see exactly which shifts it would collide with. Approve with the full picture, not after."
                Illustration={TimeOffConflictIllustration}
              />
              <FeatureCard
                title="Print-friendly schedules"
                body="One-click letter landscape PDF. Post it on the cork board, file it in the binder, email it to staff who don't want app access."
                Illustration={PrintFriendlyIllustration}
              />
              <FeatureCard
                title="Hosted in Canada"
                body="Database, files, and backups stay in the Montreal region. PIPEDA-aware. Audit log on every change. Quebec Law 25 PIA on file."
                Illustration={CanadaHostedIllustration}
              />
              <FeatureCard
                title="Fast setup"
                body="Add your first location, invite staff, drag shifts onto a month grid. The Excel file and the group chat both retire in one sitting."
                Illustration={FastSetupIllustration}
              />
            </div>
          </div>
        </section>

        {/* ===== HOW IT WORKS ===== */}
        <section className="container mx-auto px-4 py-16" aria-labelledby="how-h">
          <div className="max-w-3xl mb-10">
            <h2 id="how-h" className="text-2xl font-semibold tracking-tight">
              How it works
            </h2>
            <p className="text-base text-muted-foreground mt-2">
              Three steps to your first published month.
            </p>
          </div>
          <ol className="grid gap-6 md:grid-cols-3">
            <Step
              num="1"
              title="Add your locations"
              body="Hours, address, contact. About 2 minutes per pharmacy."
            />
            <Step
              num="2"
              title="Build the schedule"
              body="Drag shifts onto a month grid. Conflicts and gaps flagged live."
            />
            <Step
              num="3"
              title="Publish to staff"
              body="Magic-link invites. Staff see their own shifts on next sign-in."
            />
          </ol>
        </section>

        {/* ===== TRUST ===== */}
        <section
          className="border-y border-border"
          style={{ backgroundColor: 'hsl(var(--brand-navy) / 0.04)' }}
          aria-labelledby="trust-h"
        >
          <div className="container mx-auto px-4 py-12">
            <h2 id="trust-h" className="sr-only">Why pharmacies trust PharmaSync</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <TrustPill title="Hosted in Canada" body="ca-central-1 (Montreal) only." />
              <TrustPill title="PIPEDA + Law 25" body="Privacy by default. PIA on file." />
              <TrustPill title="Row-level security" body="Tenant isolation in the database." />
              <TrustPill title="Full audit log" body="Every change recorded. 7-year retention." />
            </div>
          </div>
        </section>

        {/* ===== PRICING TEASER ===== */}
        <section className="container mx-auto px-4 py-16" aria-labelledby="pricing-h">
          <div className="max-w-3xl mb-10">
            <h2 id="pricing-h" className="text-2xl font-semibold tracking-tight">
              Pricing
            </h2>
            <p className="text-base text-muted-foreground mt-2">
              Per-location pricing. {TRIAL_DAYS}-day free trial &mdash; no credit card.
              Pricing finalises after the pilot phase.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            <PricingTeaser tier="standard" plans={plans} highlight={false} />
            <PricingTeaser tier="pro" plans={plans} highlight />
            <PricingTeaser tier="enterprise" plans={plans} highlight={false} />
          </div>
          <p className="text-center mt-8">
            <Link
              to="/pricing"
              className="text-sm font-medium hover:underline"
              style={{ color: 'hsl(var(--brand-teal))' }}
            >
              See full comparison &rarr;
            </Link>
          </p>
        </section>

        {/* ===== FOUNDER NOTE ===== */}
        <section
          className="border-y border-border"
          style={{ backgroundColor: 'hsl(var(--brand-sky) / 0.10)' }}
          aria-labelledby="founder-h"
        >
          <div className="container mx-auto px-4 py-16">
            <h2 id="founder-h" className="sr-only">A note from the founder</h2>
            <blockquote
              className="max-w-3xl border-l-4 pl-6 py-2 italic text-lg leading-relaxed text-muted-foreground"
              style={{ borderColor: 'hsl(var(--brand-teal))' }}
            >
              &ldquo;Built by a Canadian who watched too many pharmacies drown their week in
              Excel sheets and group-chat schedule changes.&rdquo;
              <footer className="mt-3 text-sm not-italic" style={{ color: 'hsl(var(--brand-navy))' }}>
                &mdash; Jason Al-Farisi, founder
              </footer>
            </blockquote>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className="container mx-auto px-4 py-16" aria-labelledby="faq-h">
          <div className="max-w-3xl">
            <h2 id="faq-h" className="text-2xl font-semibold tracking-tight mb-6">
              Common questions
            </h2>
            <div className="space-y-3">
              {FAQ_ITEMS.map((item, i) => {
                const open = openFaq === i;
                return (
                  <details
                    key={item.q}
                    open={open}
                    onToggle={(e) => {
                      const target = e.currentTarget as HTMLDetailsElement;
                      setOpenFaq(target.open ? i : null);
                    }}
                    className="rounded-md border border-border bg-card"
                  >
                    <summary className="cursor-pointer list-none px-4 py-3 text-base font-medium flex items-center justify-between hover:bg-muted/30">
                      <span>{item.q}</span>
                      <span
                        className="text-lg ml-3"
                        style={{ color: 'hsl(var(--brand-teal))' }}
                        aria-hidden="true"
                      >
                        {open ? '−' : '+'}
                      </span>
                    </summary>
                    <p className="px-4 pb-4 pt-1 text-sm text-muted-foreground leading-relaxed">
                      {item.a}
                    </p>
                  </details>
                );
              })}
            </div>
          </div>
        </section>

        {/* ===== FINAL CTA ===== */}
        <section
          className="border-y border-border"
          style={{ backgroundColor: 'hsl(var(--brand-sky) / 0.18)' }}
          aria-labelledby="final-cta-h"
        >
          <div className="container mx-auto px-4 py-16 text-center">
            <h2
              id="final-cta-h"
              className="text-3xl font-semibold tracking-tight mb-4"
              style={{ color: 'hsl(var(--brand-navy))' }}
            >
              Ready to give your weekend back?
            </h2>
            <p className="text-base text-muted-foreground mb-8 max-w-xl mx-auto">
              {TRIAL_DAYS}-day free trial. No credit card. Set up your first month in roughly
              half an hour.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/auth/sign-up"
                className="inline-block rounded-md px-6 py-3 text-sm font-semibold text-white hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                style={{ backgroundColor: 'hsl(var(--brand-navy))' }}
              >
                Start your pilot
              </Link>
              <Link
                to="/auth/sign-in"
                className="inline-block px-6 py-3 text-sm font-medium hover:underline"
                style={{ color: 'hsl(var(--brand-teal))' }}
              >
                Already have an account? Sign in
              </Link>
            </div>
          </div>
        </section>

      </main>

      {/* ===== FOOTER ===== */}
      <footer className="container mx-auto px-4 py-8 text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center gap-4 mb-3">
          <Link to="/pricing" className="hover:underline">Pricing</Link>
          <Link to="/privacy" className="hover:underline">Privacy</Link>
          <Link to="/terms" className="hover:underline">Terms</Link>
          <Link to="/dpa" className="hover:underline">DPA</Link>
          <Link to="/subprocessors" className="hover:underline">Sub-processors</Link>
          <Link to="/security" className="hover:underline">Security</Link>
          <Link to="/accessibility" className="hover:underline">Accessibility</Link>
          <a href="mailto:hello@pharma-sync.ca" className="hover:underline">Contact</a>
        </div>
        <p>&copy; PharmaSync &mdash; Canadian pharmacy scheduling. Built by Blockchain PMO Inc., Ontario, Canada.</p>
      </footer>
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function FeatureCard({
  title,
  body,
  Illustration,
}: {
  title: string;
  body: string;
  Illustration: () => React.ReactNode;
}) {
  return (
    <article className="rounded-lg border border-border bg-card p-6 space-y-3 h-full">
      <div className="flex justify-center mb-2">
        <div className="w-full max-w-[220px]">
          <Illustration />
        </div>
      </div>
      <h3 className="text-lg font-semibold" style={{ color: 'hsl(var(--brand-navy))' }}>
        {title}
      </h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
    </article>
  );
}

function Step({ num, title, body }: { num: string; title: string; body: string }) {
  return (
    <li className="rounded-lg border border-border bg-card p-6 space-y-2">
      <div
        className="h-9 w-9 rounded-full flex items-center justify-center text-sm font-semibold"
        style={{ backgroundColor: 'hsl(var(--brand-navy))', color: 'white' }}
      >
        {num}
      </div>
      <h3 className="text-lg font-semibold pt-1">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
    </li>
  );
}

function TrustPill({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-md border border-border bg-card px-4 py-3">
      <p className="text-sm font-semibold" style={{ color: 'hsl(var(--brand-navy))' }}>
        {title}
      </p>
      <p className="text-xs text-muted-foreground mt-0.5">{body}</p>
    </div>
  );
}

function PricingTeaser({
  tier,
  plans,
  highlight,
}: {
  tier: SubscriptionTier;
  plans: SubscriptionPlan[];
  highlight: boolean;
}) {
  const fromDb = plans.find((p) => p.tier === tier);
  const seed = TIER_CATALOG[tier];
  const name = fromDb?.name ?? seed.name;
  const blurb = fromDb?.blurb ?? seed.blurb;
  const monthlyCAD = fromDb?.monthly_cad ?? seed.monthlyCAD;
  const cta = fromDb?.cta ?? seed.cta;

  return (
    <article
      className={`rounded-lg border bg-card p-6 flex flex-col gap-3 h-full ${
        highlight ? 'border-2' : 'border-border'
      }`}
      style={highlight ? { borderColor: 'hsl(var(--brand-navy))' } : undefined}
    >
      {highlight && (
        <span
          className="self-start rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
          style={{ backgroundColor: 'hsl(var(--brand-navy))', color: 'white' }}
        >
          Most flexible
        </span>
      )}
      <h3 className="text-xl font-semibold" style={{ color: 'hsl(var(--brand-navy))' }}>
        {name}
      </h3>
      <p className="text-sm text-muted-foreground">{blurb}</p>
      <div className="space-y-1 pt-2">
        {monthlyCAD === null ? (
          <p className="text-2xl font-semibold">Talk to us</p>
        ) : (
          <>
            <p className="text-3xl font-semibold">{formatCAD(monthlyCAD)}</p>
            <p className="text-xs text-muted-foreground">per location, per month</p>
          </>
        )}
      </div>
      <Link
        to="/pricing"
        className="mt-auto inline-block text-sm font-medium hover:underline pt-3"
        style={{ color: 'hsl(var(--brand-teal))' }}
      >
        {cta} &rarr;
      </Link>
    </article>
  );
}

const FAQ_ITEMS = [
  {
    q: 'Where is my data hosted?',
    a: "Database, files, and backups in Supabase's ca-central-1 (Montreal) region. The full sub-processor list is at /subprocessors.",
  },
  {
    q: 'Is my data safe?',
    a: 'Encrypted in transit (TLS 1.2+) and at rest (AES-256). Row-level security at the database means a bug in the API can’t leak data across organizations. Every change to your data is recorded in an owner-visible audit log. PIPEDA + Quebec Law 25 aligned by default.',
  },
  {
    q: "What's the pilot program?",
    a: 'A small group of Canadian pharmacies who help us refine the product. We work with you closely and your feedback shapes what ships next. Pricing finalises after the pilot phase.',
  },
  {
    q: 'What if I cancel?',
    a: 'Export everything (staff, schedules, audit log) on the way out. We delete or anonymize within 90 days. No lock-in, no win-back emails.',
  },
  {
    q: 'Can my staff see what I see?',
    a: 'No. Staff see their own shifts and their own time-off requests. Managers see the schedule for their assigned locations. Only owners see the full audit log and admin surfaces.',
  },
];
